import * as Yup from 'yup';
import { slugValidator } from '../../../../../utils/validators';

// The Overview form's validation rules, kept in their own module so they can be exercised directly. Several
// of them guard states the UI cannot produce — you cannot clear a required select — so driving the form is
// not enough to prove they are present, and a silent drop is exactly how they went missing once already.
export const overviewValidationRules = {
  name: Yup.string().label('Name').max(255).required(),
  key: Yup.string()
    .label('Project URL ID')
    .matches(slugValidator.expression, { message: slugValidator.message })
    .max(255)
    .required(),
  code: Yup.string().label('Code').max(255).nullable(),
  description: Yup.string().label('Description').max(5000).nullable(),
  poNumber: Yup.string().label('PO Number').max(255).nullable(),
  billingTypeId: Yup.string().label('Billing Type').required(),
  client: Yup.mixed().label('Client').required(),
  currency: Yup.string().label('Currency').required(),
  statusId: Yup.string().label('Status').required(),
  cloudFolderUrl: Yup.string()
    .label('Cloud Folder')
    .url('Please include http:// or https:// protocols in your URL')
    .max(1024)
    .nullable(),
  completedOn: Yup.date()
    .label('Completed On')
    .nullable()
    .when('statusId', (statusId, schema) => (statusId === 'completed' ? schema.required() : schema)),
  start: Yup.date().label('Start Date').nullable(),
  end: Yup.date()
    .label('End Date')
    .nullable()
    .when('start', (start, schema) =>
      start ? schema.min(Yup.ref('start'), 'End Date must be after Start Date') : schema,
    ),
  useBudget: Yup.bool()
    .label('Track overall budget')
    .when(['revenueRecognitionMethod', 'billingTypeId'], (revenueRecognitionMethod, billingTypeId, schema) =>
      ['automated_hours', 'automated_tm_basis', 'automated_services_cost'].includes(revenueRecognitionMethod) &&
      ['fixed', 'fixed_recurring'].includes(billingTypeId)
        ? schema.oneOf([true], 'An overall budget is required for the selected Revenue Recognition Method.')
        : schema,
    ),
  accountingNotes: Yup.string().label('Accounting Notes').max(5000).nullable(),

  // Invoice Details
  emails: Yup.array().of(Yup.string().email().max(255).required()).label('To Email Address').ensure(),
  ccEmails: Yup.array().of(Yup.string().email().max(255).required()).label('Cc Email Address').ensure(),
  // The form is seeded with the effective term (inherited from the client, business unit or workspace when
  // not custom), so this only blocks a save where the whole inheritance chain is empty — a project that
  // could not be invoiced. Same rule the drawer enforced.
  invoicePaymentTerm: Yup.object().label('Payment Terms').required().nullable(),
  streetAddress: Yup.string().label('Invoice Street Address').max(5000).nullable(),
  invoiceSubject: Yup.string().label('Invoice Subject').max(255).nullable(),
  invoiceNotes: Yup.string().label('Invoice Note').max(5000).nullable(),
  invoiceEmailSubject: Yup.string().label('Invoice Email Subject').max(255).nullable(),
  invoiceEmailBody: Yup.string().label('Invoice Email Body').max(5000).nullable(),
};

// Which rules each section form enforces. A section saves the whole project but only renders its own fields,
// so validating the rest would reject the save over a field the member cannot see, let alone correct.
// Rules that read a sibling still resolve it — the values object handed to Yup carries every field, whether
// or not the schema names it.
const sectionRules = {
  'basic-information': ['name', 'key', 'code', 'description', 'client', 'statusId', 'completedOn', 'start', 'end'],
  settings: ['useBudget'],
  'time-tracking-rules': [],
  'approval-options': [],
  billing: ['billingTypeId', 'currency', 'poNumber', 'accountingNotes'],
  'invoice-details': [
    'emails',
    'ccEmails',
    'invoicePaymentTerm',
    'streetAddress',
    'invoiceSubject',
    'invoiceNotes',
    'invoiceEmailSubject',
    'invoiceEmailBody',
  ],
  'cloud-folder': ['cloudFolderUrl'],
  slack: [],
  quickbooks: [],
};

export function sectionValidationSchema(section) {
  const rules = {};
  for (const field of sectionRules[section] ?? []) rules[field] = overviewValidationRules[field];
  return Yup.object().shape(rules);
}

export const overviewValidationSchema = Yup.object().shape(overviewValidationRules);

export default overviewValidationSchema;
